// Affiliate referral tracking — persists an affiliate ref code across the 180-day window
// advertised on the affiliate program page. Uses BOTH localStorage (primary) and a first-party
// cookie (fallback when localStorage is cleared by the browser / private mode).
//
// Shape stored: { code: "JOSH8A3F2C", clickedAt: 1714678400000 }
// The clickedAt timestamp lets us enforce the window server- and client-side.

export const AFFILIATE_WINDOW_DAYS = 180;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'affiliate_ref';
const COOKIE_NAME = 'renew_affiliate_ref';
const COOKIE_MAX_AGE_SECONDS = AFFILIATE_WINDOW_DAYS * 24 * 60 * 60;
const CLICK_DEDUPE_KEY = 'affiliate_click_session';

function setCookie(value) {
  // SameSite=Lax so it survives same-site navigation and top-level navs from social apps
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax${secure}`;
}

function readCookie() {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE_NAME}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

function clearCookie() {
  document.cookie = `${COOKIE_NAME}=; max-age=0; path=/`;
}

/** Persist a fresh click. Idempotent within the same browser session (won't double-ping the server). */
export function captureAffiliateRef(code) {
  if (!code || typeof code !== 'string') return;
  const cleanCode = code.trim().toUpperCase().slice(0, 32);
  if (!cleanCode) return;

  const payload = JSON.stringify({ code: cleanCode, clickedAt: Date.now() });

  try {
    localStorage.setItem(STORAGE_KEY, payload);
  } catch {
    // localStorage may throw in private mode / quota; the cookie acts as fallback
  }
  setCookie(payload);

  // Only ping /track once per session per code to avoid inflating total_clicks
  try {
    const sessionKey = sessionStorage.getItem(CLICK_DEDUPE_KEY);
    if (sessionKey !== cleanCode) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliate/track/${cleanCode}`).catch(() => {});
      sessionStorage.setItem(CLICK_DEDUPE_KEY, cleanCode);
    }
  } catch {
    // sessionStorage unavailable — fall through, fire the ping anyway
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliate/track/${cleanCode}`).catch(() => {});
  }
}

/** Read the active ref if within window, else null. Cleans up expired entries. */
export function getActiveAffiliateRef() {
  let raw = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch { /* private mode */ }
  if (!raw) raw = readCookie();
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Legacy plain-string format from pre-window tracking — treat as just-clicked so existing
    // in-flight referrals don't get lost on first deploy.
    parsed = { code: String(raw).toUpperCase(), clickedAt: Date.now() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed)); } catch { /* noop */ }
  }

  if (!parsed.code) return null;
  const ageMs = Date.now() - (parsed.clickedAt || 0);
  if (ageMs > AFFILIATE_WINDOW_DAYS * MS_PER_DAY) {
    clearAffiliateRef();
    return null;
  }
  return { code: parsed.code, clickedAt: parsed.clickedAt, daysLeft: Math.max(0, Math.ceil((AFFILIATE_WINDOW_DAYS * MS_PER_DAY - ageMs) / MS_PER_DAY)) };
}

/** Clear after successful order attribution. */
export function clearAffiliateRef() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  clearCookie();
}
